import { ManifestCreateFields } from "./manifest-create-fields";
import { useState, useMemo, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useQuery, useMutation } from "@tanstack/react-query";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  CheckmarkCircle02Icon,
  Folder01Icon,
  UserMultiple02Icon,
} from "@hugeicons/core-free-icons";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { SearchableCombobox } from "@/components/ui/searchable-combobox";
import { useDebounce } from "@/lib/hooks/use-debounce";
import { useAuth } from "@/lib/auth/use-auth";
import { createOrder, createProjectOrder } from "../api/orders-api";
import { getCustomers } from "@/features/accounts/api/accounts-api";
import { listCustomerAccounts } from "@/features/customers/api/customers-api";
import { useProjectSearch } from "@/features/projects/queries/projects-queries";
import type { CustomerAccountListItem } from "@/features/customers/types/customer.types";

type Mode = "legacy" | "project" | "manifest";

interface LegacyCustomer {
  id: number;
  display_name: string;
}

interface CreateOrderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateOrderDialog({ open, onOpenChange }: CreateOrderDialogProps) {
  const navigate = useNavigate();
  const { role } = useAuth();
  const isStaff = role === "ADMIN" || role === "STAFF";

  const [mode, setMode] = useState<Mode>("legacy");
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");

  // ─── Legacy mode ─────────────────────────────────────────────────────────
  const [legacyCustomerId, setLegacyCustomerId] = useState("");
  const [legacyCustomerSearch, setLegacyCustomerSearch] = useState("");
  const [legacyCustomerOpen, setLegacyCustomerOpen] = useState(false);
  const legacyCustomerSearchRef = useRef<HTMLInputElement>(null);

  // ─── Project mode ────────────────────────────────────────────────────────
  const [accountId, setAccountId] = useState<number | null>(null);
  const [accountSearch, setAccountSearch] = useState("");
  const [accountOpen, setAccountOpen] = useState(false);
  const accountSearchRef = useRef<HTMLInputElement>(null);
  const [projectId, setProjectId] = useState<number | null>(null);
  const [projectLabel, setProjectLabel] = useState("");
  const [projectSearch, setProjectSearch] = useState("");
  const debouncedProjectSearch = useDebounce(projectSearch, 200);

  // Flip mode + reset the fields owned by the mode we're leaving.
  // Done inline (instead of a useEffect on `mode`) so the React-hooks
  // lint rule against state mutation inside effects stays happy.
  function switchMode(next: Mode) {
    if (next === mode) return;
    if (next === "legacy") {
      setAccountId(null);
      setAccountSearch("");
      setProjectId(null);
      setProjectLabel("");
      setProjectSearch("");
    } else {
      setLegacyCustomerId("");
      setLegacyCustomerSearch("");
    }
    setMode(next);
  }

  // ─── Data hooks ──────────────────────────────────────────────────────────
  const { data: legacyCustomers } = useQuery({
    queryKey: ["customers"],
    queryFn: getCustomers,
    enabled: isStaff && open && mode === "legacy",
  });
  const legacyCustomerList = useMemo<LegacyCustomer[]>(
    () => (Array.isArray(legacyCustomers) ? (legacyCustomers as LegacyCustomer[]) : []),
    [legacyCustomers],
  );
  const filteredLegacyCustomers = useMemo(() => {
    if (!legacyCustomerSearch) return legacyCustomerList;
    const q = legacyCustomerSearch.toLowerCase();
    return legacyCustomerList.filter((c) =>
      c.display_name.toLowerCase().includes(q),
    );
  }, [legacyCustomerList, legacyCustomerSearch]);
  const selectedLegacyCustomer = legacyCustomerList.find(
    (c) => String(c.id) === legacyCustomerId,
  );

  const { data: accountPage } = useQuery({
    queryKey: ["customer-accounts", { page_size: 500, account_type: "CUSTOMER" }],
    queryFn: () =>
      listCustomerAccounts({ page_size: 500, account_type: "CUSTOMER" }),
    enabled: isStaff && open && mode === "project",
  });
  const accountList = useMemo<CustomerAccountListItem[]>(
    () => accountPage?.results ?? [],
    [accountPage],
  );
  const filteredAccounts = useMemo(() => {
    if (!accountSearch) return accountList;
    const q = accountSearch.toLowerCase();
    return accountList.filter((a) => a.name.toLowerCase().includes(q));
  }, [accountList, accountSearch]);
  const selectedAccount = accountList.find((a) => a.id === accountId);

  const { data: projectPage, isLoading: projectsLoading } = useProjectSearch(
    debouncedProjectSearch,
    {
      enabled: isStaff && open && mode === "project" && accountId !== null,
      customer: accountId ?? undefined,
    },
  );
  const projectOptions = projectPage?.results ?? [];

  // ─── Focus search input when its popover opens ───────────────────────────
  useEffect(() => {
    if (legacyCustomerOpen) {
      setTimeout(() => legacyCustomerSearchRef.current?.focus(), 0);
    }
  }, [legacyCustomerOpen]);
  useEffect(() => {
    if (accountOpen) {
      setTimeout(() => accountSearchRef.current?.focus(), 0);
    }
  }, [accountOpen]);

  // ─── Mutations ───────────────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) => createOrder(payload),
  });
  const createProjectMutation = useMutation({
    mutationFn: (vars: { projectId: number; name: string }) =>
      createProjectOrder(vars),
  });
  const isPending = createMutation.isPending || createProjectMutation.isPending;

  // ─── Helpers ─────────────────────────────────────────────────────────────
  function resetAll() {
    setName("");
    setAmount("");
    setLegacyCustomerId("");
    setLegacyCustomerSearch("");
    setAccountId(null);
    setAccountSearch("");
    setProjectId(null);
    setProjectLabel("");
    setProjectSearch("");
    setMode("legacy");
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (mode === "manifest") {
      createMutation.mutate(
        {
          pricing_mode: "LUMP_SUM",
          name: name.trim(),
          lump_sum_amount: amount || null,
          project_id: projectId,
        },
        {
          onSuccess: (order) => {
            onOpenChange(false);
            resetAll();
            toast.success("Lump-sum order created");
            navigate(`/orders/${order.id}`);
          },
        },
      );
      return;
    }
    if (mode === "legacy") {
      const payload: Record<string, unknown> = { name, groups: [] };
      if (legacyCustomerId) payload.customer = Number(legacyCustomerId);
      createMutation.mutate(payload, {
        onSuccess: (data) => {
          const order = data as { id: number };
          toast.success("Order created");
          onOpenChange(false);
          resetAll();
          navigate(`/orders/${order.id}`);
        },
      });
      return;
    }
    if (projectId === null) return;
    createProjectMutation.mutate(
      { projectId, name },
      {
        onSuccess: (order) => {
          toast.success("Project order created");
          onOpenChange(false);
          resetAll();
          navigate(`/orders/${order.id}`);
        },
      },
    );
  }

  // ─── Derived button + label state ────────────────────────────────────────
  // Legacy customer is required only when staff is picking on behalf of
  // someone. A non-staff customer creates an order for themselves —
  // the backend resolves the customer from ``request.user``.
  const submitDisabled =
    isPending ||
    (!name.trim() && mode !== "manifest") ||
    (mode === "legacy"
      ? isStaff && !legacyCustomerId
      : mode === "project" && projectId === null);
  const submitLabel = isPending
    ? "Creating…"
    : mode !== "project"
      ? "Create order"
      : "Create project order";

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) resetAll();
        onOpenChange(v);
      }}
    >
      <DialogContent className="max-h-[85svh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Create Order</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* ─── Mode picker — staff/employee only ──────────────────────── */}
          {isStaff && (
            <>
              <div>
                <div className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  Order type
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <ModeCard
                    active={mode === "legacy"}
                    icon={UserMultiple02Icon}
                    title="Legacy customer"
                    description="Pick an existing customer user. Submits + bills through the standard customer flow."
                    onClick={() => switchMode("legacy")}
                  />
                  <ModeCard
                    active={mode === "project"}
                    icon={Folder01Icon}
                    title="Project order"
                    description="Pick a customer account + project. Staff drives the lifecycle; billing via project invoices."
                    onClick={() => switchMode("project")}
                  />
                  <ModeCard
                    active={mode === "manifest"}
                    icon={Folder01Icon}
                    title="Lump sum"
                    description="One entered price. Import an ORDX file or add rooms and items by hand. Optionally link a project."
                    onClick={() => switchMode("manifest")}
                  />
                </div>
              </div>

              <div className="border-t" />
            </>
          )}

          {/* ─── Shared: order name ──────────────────────────────────────── */}
          <div className="space-y-1">
            <Label htmlFor="order-name">Order name</Label>
            <Input
              id="order-name"
              placeholder="e.g. Kitchen Remodel"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required={mode !== "manifest"}
              autoFocus
            />
            {mode === "manifest" && (
              <p className="text-xs text-muted-foreground">
                Optional if you import ORDX. Leave it blank to use the job name
                from the first file you import.
              </p>
            )}
          </div>

          {/* ─── Mode-specific form ──────────────────────────────────────── */}
          {mode === "manifest" ? (
            <ManifestCreateFields
              amount={amount}
              setAmount={setAmount}
              projectId={projectId}
              setProjectId={setProjectId}
            />
          ) : mode === "legacy" ? (
            <div key="legacy" className="space-y-4 animate-in fade-in-50 duration-150">
              {isStaff && (
                <div className="space-y-1">
                  <Label>Customer</Label>
                  <Popover open={legacyCustomerOpen} onOpenChange={setLegacyCustomerOpen}>
                    <PopoverTrigger className="flex h-7 w-full items-center justify-between rounded-md border border-input bg-input/20 px-2 py-0.5 text-sm md:text-xs/relaxed cursor-pointer hover:bg-muted/50">
                      <span
                        className={
                          selectedLegacyCustomer ? "text-foreground" : "text-muted-foreground"
                        }
                      >
                        {selectedLegacyCustomer?.display_name ?? "Select a customer…"}
                      </span>
                      <ChevronTinyIcon />
                    </PopoverTrigger>
                    <PopoverContent className="w-[var(--anchor-width)] p-0" align="start">
                      <div className="border-b p-2">
                        <Input
                          ref={legacyCustomerSearchRef}
                          placeholder="Search customers…"
                          value={legacyCustomerSearch}
                          onChange={(e) => setLegacyCustomerSearch(e.target.value)}
                          className="h-8 text-sm"
                        />
                      </div>
                      <div className="max-h-60 overflow-y-auto">
                        {filteredLegacyCustomers.length === 0 ? (
                          <div className="p-3 text-center text-xs text-muted-foreground">
                            No customers found.
                          </div>
                        ) : (
                          filteredLegacyCustomers.map((c) => (
                            <button
                              key={c.id}
                              type="button"
                              className={`w-full cursor-pointer px-3 py-2 text-left text-sm hover:bg-muted/50 ${
                                String(c.id) === legacyCustomerId
                                  ? "bg-muted font-medium"
                                  : ""
                              }`}
                              onClick={() => {
                                setLegacyCustomerId(String(c.id));
                                setLegacyCustomerOpen(false);
                                setLegacyCustomerSearch("");
                              }}
                            >
                              {c.display_name}
                            </button>
                          ))
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              )}

            </div>
          ) : (
            <div key="project" className="space-y-4 animate-in fade-in-50 duration-150">
              <div className="space-y-1">
                <Label>Customer account</Label>
                <Popover open={accountOpen} onOpenChange={setAccountOpen}>
                  <PopoverTrigger className="flex h-7 w-full items-center justify-between rounded-md border border-input bg-input/20 px-2 py-0.5 text-sm md:text-xs/relaxed cursor-pointer hover:bg-muted/50">
                    <span
                      className={
                        selectedAccount ? "text-foreground" : "text-muted-foreground"
                      }
                    >
                      {selectedAccount?.name ?? "Select a customer account…"}
                    </span>
                    <ChevronTinyIcon />
                  </PopoverTrigger>
                  <PopoverContent className="w-[var(--anchor-width)] p-0" align="start">
                    <div className="border-b p-2">
                      <Input
                        ref={accountSearchRef}
                        placeholder="Search accounts…"
                        value={accountSearch}
                        onChange={(e) => setAccountSearch(e.target.value)}
                        className="h-8 text-sm"
                      />
                    </div>
                    <div className="max-h-60 overflow-y-auto">
                      {filteredAccounts.length === 0 ? (
                        <div className="p-3 text-center text-xs text-muted-foreground">
                          No accounts found.
                        </div>
                      ) : (
                        filteredAccounts.map((a) => (
                          <button
                            key={a.id}
                            type="button"
                            className={`w-full cursor-pointer px-3 py-2 text-left text-sm hover:bg-muted/50 ${
                              a.id === accountId ? "bg-muted font-medium" : ""
                            }`}
                            onClick={() => {
                              setAccountId(a.id);
                              setAccountOpen(false);
                              setAccountSearch("");
                              // Switching accounts invalidates the
                              // previously-picked project (which belonged
                              // to the old account).
                              if (
                                projectId !== null &&
                                projectOptions.find((p) => p.id === projectId)?.customer
                                  ?.id !== a.id
                              ) {
                                setProjectId(null);
                                setProjectLabel("");
                              }
                            }}
                          >
                            {a.name}
                          </button>
                        ))
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <Label>Project</Label>
                  {projectId !== null && (
                    <button
                      type="button"
                      className="cursor-pointer text-xs text-muted-foreground hover:text-destructive"
                      onClick={() => {
                        setProjectId(null);
                        setProjectLabel("");
                      }}
                    >
                      Clear
                    </button>
                  )}
                </div>
                {accountId === null ? (
                  <div className="flex items-center gap-2 rounded-md border border-dashed bg-muted/30 px-3 py-2.5 text-xs text-muted-foreground">
                    <HugeiconsIcon icon={Folder01Icon} size={14} />
                    Pick a customer account to see its projects.
                  </div>
                ) : (
                  <SearchableCombobox
                    value={projectId}
                    onChange={(v) => {
                      if (v === null) {
                        setProjectId(null);
                        setProjectLabel("");
                        return;
                      }
                      const id = Number(v);
                      setProjectId(id);
                      const picked = projectOptions.find((p) => p.id === id);
                      if (picked) setProjectLabel(picked.name);
                    }}
                    options={projectOptions}
                    getOptionValue={(p) => p.id}
                    getOptionLabel={(p) => p.name}
                    searchValue={projectSearch}
                    onSearchChange={setProjectSearch}
                    placeholder={`Select a project under ${selectedAccount?.name ?? ""}`}
                    searchPlaceholder="Search projects..."
                    emptyMessage="No projects on this account."
                    fallbackLabel={projectLabel}
                    loading={projectsLoading}
                  />
                )}
              </div>
            </div>
          )}

          {/* ─── Footer ──────────────────────────────────────────────────── */}
          <div className="flex justify-end gap-2 pt-1">
            <DialogClose render={<Button variant="outline" className="cursor-pointer" />}>
              Cancel
            </DialogClose>
            <Button
              type="submit"
              className="cursor-pointer"
              disabled={submitDisabled}
            >
              {submitLabel}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Local helpers ─────────────────────────────────────────────────────────

function ModeCard({
  active,
  icon,
  title,
  description,
  onClick,
}: {
  active: boolean;
  icon: typeof Folder01Icon;
  title: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group relative cursor-pointer rounded-lg border p-3.5 text-left transition-all ${
        active
          ? "border-primary bg-primary/5 ring-2 ring-primary/30"
          : "border-input bg-card hover:-translate-y-px hover:border-primary/40 hover:bg-muted/40"
      }`}
    >
      {active && (
        <span className="absolute right-2.5 top-2.5 inline-flex items-center gap-1 rounded-full bg-primary/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-primary">
          <HugeiconsIcon icon={CheckmarkCircle02Icon} size={10} />
          Active
        </span>
      )}
      <div
        className={`mb-2 grid h-8 w-8 place-items-center rounded-md transition-colors ${
          active
            ? "bg-primary text-primary-foreground"
            : "bg-muted text-muted-foreground group-hover:bg-primary/15 group-hover:text-primary"
        }`}
      >
        <HugeiconsIcon icon={icon} size={16} />
      </div>
      <div
        className={`text-sm font-medium leading-snug ${
          active ? "text-foreground" : "text-foreground/85"
        }`}
      >
        {title}
      </div>
      <div className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
        {description}
      </div>
    </button>
  );
}

function ChevronTinyIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" className="opacity-50">
      <path
        d="M3 5l3 3 3-3"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}
